<template>
  <aside class="sidebar">
    <button
      class="button is-fullwidth is-rounded is-hidden-tablet"
      @click="open = !open"
    >
      {{ $texts('selectCategories', 'Kategorien auswählen') }}
    </button>
    <transition name="fade">
      <div v-show="open" class="sidebar-menu">
        <ul class="hierarchical-menu">
          <li
            v-for="(item, index) in getSublevel"
            :key="index"
            :class="{
              'has-submenu': item.subtree.length,
              'is-active': isActive(item.link.label),
            }"
          >
            <VuepalLink class="link" :to="item?.link?.url?.path">{{
              item.link.label
            }}</VuepalLink>
            <ul
              v-if="item.subtree.length && isActive(item.link.label)"
              class="hierarchical-menu-submenu"
            >
              <li v-for="(subitem, subindex) in item.subtree" :key="subindex">
                <VuepalLink :to="subitem?.link?.url?.path">{{
                  subitem.link.label
                }}</VuepalLink>
              </li>
            </ul>
          </li>
        </ul>
        <div class="sidebar-menu-footer is-hidden-tablet">
          <button
            class="button is-fullwidth is-rounded is-primary"
            @click="open = false"
          >
            {{ $texts('showResults', 'Resultate anzeigen') }}
          </button>
        </div>
      </div>
    </transition>
  </aside>
</template>

<script setup lang="ts">
import type { MenuLinkTreeElementFirstFragment } from '#graphql-operations'

const props = defineProps<{
  menu: MenuLinkTreeElementFirstFragment[]
}>()

const { $texts } = useNuxtApp()
const open = ref(false)

const breadcrumb = await useBreadcrumbLinks()

const getSublevel = computed(() => {
  const rootLevel = breadcrumb.value[1]

  if (rootLevel) {
    const path = rootLevel.url?.path || ''
    const currentItem = getSubMenuItem(props.menu, path) || {}

    if (currentItem.subtree) {
      return currentItem.subtree as MenuLinkTreeElementFirstFragment[]
    }
  }

  return []
})

const getSubMenuItem = (
  subMenuItems: MenuLinkTreeElementFirstFragment[],
  currentPath: string,
): any => {
  if (subMenuItems) {
    for (let i = 0; i < subMenuItems.length; i++) {
      if (subMenuItems[i].link.url?.path === currentPath) {
        return subMenuItems[i]
      }
      // @ts-ignore
      const found = getSubMenuItem(subMenuItems[i].subtree, currentPath)
      if (found) return found
    }
  }
}

const isActive = (text: string) => {
  return breadcrumb.value.some((v) => v.title === text)
}
</script>

<style lang="scss">
@import '~/assets/scss/helper/mixins.scss';

$hierarchical-menu-padding: 1em;
.sidebar-menu {
  @include mobile {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: white;
    z-index: 100000;
    overflow: auto;
    &.fade-enter-active,
    &.fade-leave-active {
      transition: 0.3s;
    }
    &.fade-enter,
    &.fade-leave-to {
      transform: translateY(100%);
    }
  }
  @include tablet {
    display: block !important;
  }
}

.sidebar-menu-footer {
  position: sticky;
  bottom: 0;
  z-index: 10;
  background: white;
  border-top: 1px solid $border;
  padding: 1rem 1.5rem;
}

.hierarchical-menu {
  margin-left: -$hierarchical-menu-padding;

  @include mobile {
    padding: 1.5rem;
  }

  > li {
    > a {
      font-size: rem(16);
      padding: 0.25em 0;
      padding-left: $hierarchical-menu-padding;
      font-weight: $weight-extrabold;
    }
    &.has-submenu {
      > a {
        &:before {
          content: '';
          width: 0;
          height: 0;
          border-style: solid;
          border-width: 0.3em 0 0.3em 0.425em;
          border-color: transparent transparent transparent currentColor;
          position: absolute;
          left: 0;
          top: 0.7em;
        }
      }
    }
    &.is-active {
      &.has-submenu > a:before {
        transform: rotate(90deg);
      }
      > a {
        color: $black;
      }
    }
  }
  a {
    display: block;
    position: relative;
  }
}
.hierarchical-menu-submenu {
  padding-left: $hierarchical-menu-padding;
  a {
    font-size: $size-7;
    color: $text-light;
    font-weight: $weight-bold;
    padding: 0.25em 0;
    &.nuxt-link-active {
      font-weight: $weight-extrabold;
    }
  }
}
</style>
